import { installLensHost } from "./installLensHost.js";
import { ClientMessageSchema } from "@lenscore-pipeline/host-protocol";
/**
 * Install a postMessage-based LensHost. Outbound messages (commandResult /
 * state / ready / log) are posted to `targetWindow` (default: `window.parent`).
 * Inbound messages are validated against the protocol's ClientMessageSchema;
 * `command`-typed messages are dispatched into the renderer via
 * `window.lensCommand`. All other valid types are forwarded but otherwise
 * ignored — postMessage isn't the right channel for cross-frame state echo
 * back to the renderer.
 *
 * Designed as the safe, zero-config default: posting to `window.parent` in
 * a top-level tab posts to self (harmless — nothing listens), while in an
 * iframe it reaches the embedding parent as expected.
 *
 * Inbound (from parent → page):
 *   { type: "command", command, payload?, requestId? }
 *
 * Outbound (page → parent):
 *   { type: "commandResult", requestId, ok, result?, error? }
 *   { type: "state", ready?, source?, frameCount?, activeLenses?, error? }
 *   { type: "ready", payload? }
 *   { type: "log", level, message, detail? }
 *
 * No-op when `window` is undefined (SSR contexts).
 *
 * @param {{
 *   targetWindow?: Window,
 *   targetOrigin?: string,
 * }} [options]
 */
export function installLensHostPostMessage({ targetWindow, targetOrigin = "*", } = {}) {
    if (typeof window === "undefined")
        return;
    const out = targetWindow ?? window.parent ?? window;
    installLensHost({
        sendToHost(message) {
            try {
                out.postMessage(message, targetOrigin);
            }
            catch {
                // postMessage can throw if the target window has been closed
                // (cross-process iframe teardown, etc.). Drop the message silently
                // rather than crash the renderer.
            }
        },
    });
    window.addEventListener("message", (event) => {
        const parsed = ClientMessageSchema.safeParse(event?.data);
        if (!parsed.success)
            return;
        if (parsed.data.type !== "command")
            return;
        window.lensCommand?.(parsed.data.command, parsed.data.payload, parsed.data.requestId);
    });
}
//# sourceMappingURL=installLensHostPostMessage.js.map